"use client";

import { useEffect, useState } from "react";
import { locateByIp } from "./geo";

/**
 * Whether to show the international helpline instead of the domestic 1424.
 *
 * Deliberately one-directional: 1424 is the answer unless a LIVE lookup
 * positively reports a country other than Pakistan.
 *
 * This replaces a server-side decision made from `geoip-lite`, a bundled
 * OFFLINE database. That database resolved a real Pakistani visitor's ISP
 * range to another country, so someone sitting in Rawalpindi was shown the
 * international number - while the weather strip on the very same page read
 * "Rawalpindi" correctly, because it uses the live service this hook calls.
 * Two different geo sources, disagreeing, on one page.
 *
 * The bias is the point. 1424 is a domestic short code and the correct number
 * for everyone inside Pakistan, which is most of this site's traffic. Briefly
 * showing 1424 to a foreign visitor costs them a moment; showing
 * +92 51 8911005 to a Pakistani visitor is the bug being fixed. So absence of
 * proof - no lookup yet, no country field, provider down - always means 1424.
 *
 * Returns false during SSR and until the lookup resolves, so the domestic
 * number is what renders by default and what a visitor without JS keeps.
 *
 * locateByIp() is already memoised in localStorage and is called by the
 * weather strip on the same page, so this adds no extra network request in
 * practice - it reads the same cached answer.
 */
export function useIsInternationalVisitor(): boolean {
  const [isInternational, setIsInternational] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const located = await locateByIp();
      if (cancelled) return;
      const code = located?.countryCode?.trim().toUpperCase();
      // Only ever flips TO international, never back to domestic — the
      // default already is domestic.
      if (code && code !== "PK") setIsInternational(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return isInternational;
}
